import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import DoorCta from './DoorCta';
import useDoorPage, { studioHrefFor } from '../hooks/useDoorPage';
import { DOOR_PHOTO_SEQUENCE } from '../doorAttribution';
import { RETENTION_DAYS } from '../photoUpload/caps';
import { PRICING_TIERS } from '../pricing/plans';
import './DoorPage.css';

// Photo-sequence door (#384). Traffic door #2 from the 2026-07-25 positioning
// study: the head of "image sequence to video" belongs to the general-purpose
// converters, so this page goes after the DEVICE TAIL (GoPro Time Lapse Photo
// mode, Brinno, trail/Wyze cams, Raspberry Pi rigs) and after SCALE — the one
// axis where a browser tool that takes 10,000 frames beats a 100-file uploader.
//
// Honesty contract (see #378/#384): every capability named here has to be
// reachable in the shipped UI. The numbers are imported from the modules that
// ENFORCE them (caps.js, pricing/plans.js) rather than retyped, so a limit
// change can never leave a stale promise on a marketing page. #342 removed the
// per-project photo/byte ceilings entirely, so the only figure this page may
// quote as a limit is the tier's POOLED storage allowance.
//
// The studio path is deep-linked only: this page adds no upload, operate or
// download surface of its own. Signed-out CTAs stash attribution and open the
// app-level sign-in modal; signed-in CTAs go straight to /studio.

const FREE_TIER = PRICING_TIERS.find((tier) => tier.id === 'free');
const SOLO_TIER = PRICING_TIERS.find((tier) => tier.id === 'solo_founding');
const PRO_TIER = PRICING_TIERS.find((tier) => tier.id === 'pro_founding');

// Mirrors FPS_OPTIONS / DEFAULT_FPS in components/project/PhotoCompileCta.js,
// which the compile_photos callable re-validates server-side. Copy only.
const FPS_CHOICES = '24, 30, or 60 fps';
const DEFAULT_FPS_LABEL = '24 fps';

// The one enforced ceiling (#342): a per-ACCOUNT pooled storage allowance,
// shared across an unlimited number of projects. Read off the pricing catalog
// the entitlements are derived from, never retyped.
const FREE_POOL_LABEL = `${FREE_TIER.storageGb} GB`;
const SOLO_POOL_LABEL = `${SOLO_TIER.storageGb} GB`;
const PRO_POOL_LABEL = `${PRO_TIER.storageGb} GB`;

const SECTION_IDS = [
  'door_hero',
  'door_situation',
  'door_scale',
  'door_gopro',
  'door_brinno',
  'door_trailcam',
  'door_pi',
  'door_how',
  'door_free',
  'door_faq',
  'door_final_cta',
];

const TOC = [
  { id: 'door_situation', label: 'The gap your camera leaves you' },
  { id: 'door_scale', label: `How many photos you can actually upload (${FREE_POOL_LABEL} free)` },
  { id: 'door_gopro', label: 'GoPro Time Lapse Photo mode' },
  { id: 'door_brinno', label: 'Brinno timelapse cameras' },
  { id: 'door_trailcam', label: 'Trail cameras and Wyze cams' },
  { id: 'door_pi', label: 'Raspberry Pi camera rigs' },
  { id: 'door_how', label: 'How it works, step by step' },
  { id: 'door_free', label: `What the free account includes` },
  { id: 'door_faq', label: 'FAQ' },
];

const STEPS = [
  {
    name: 'Create a free account',
    text: 'No credit card. An account is required before anything is uploaded — there is no anonymous upload surface.',
  },
  {
    name: 'Start a new project',
    text: 'One project per shoot. It holds your stills, the compiled video, and every edit you render from it.',
  },
  {
    name: 'Upload your stills on the Footage tab',
    text: 'Drop the whole folder. Photos are ordered by EXIF capture time, with the file date and then the filename as fallbacks, and anything already uploaded is skipped instead of duplicated.',
  },
  {
    name: 'Pick a frame rate',
    text: `${FPS_CHOICES}, ${DEFAULT_FPS_LABEL} by default. That is the only setting you have to touch to get a video.`,
  },
  {
    name: 'Compile the sequence into a video',
    text: 'The compile runs on our workers and writes the video back into the project as its source footage.',
  },
  {
    name: 'Apply edits on the Edits tab',
    text: 'Trim the stillness, fix flicker, and set transitions on the compiled video, then render.',
  },
  {
    name: 'Download your render',
    text: 'The finished edit is yours to download. Renders on the free plan carry a Tweedia watermark; a paid plan removes it.',
  },
];

const FAQ = [
  {
    q: 'How do I turn a photo sequence into a video?',
    a: `Create a free Tweedia Studio account, start a project, and drop your stills onto the project's Footage tab. Pick a frame rate (${FPS_CHOICES}) and compile — the sequence becomes a video inside the project, which you then edit and render. Nothing is installed; the upload and the edit both happen in the browser.`,
  },
  {
    q: `How many images can I convert at once?`,
    a: `There is no per-project frame limit — a project holds as many stills as your account's pooled storage allowance covers, and you can have as many projects as you like. The allowance is the one enforced ceiling (${FREE_POOL_LABEL} free, ${SOLO_POOL_LABEL} on ${SOLO_TIER.name}, ${PRO_POOL_LABEL} on ${PRO_TIER.name}) and it is shared across every project on the account. The uploader adds your batch to what you have already stored and tells you before a single file moves if it will not fit.`,
  },
  {
    q: 'What if my photos have no EXIF data?',
    a: 'They still work. Capture time comes from EXIF when it is there; when it is missing, the file date is used instead, and photos that share a timestamp are ordered by filename. Cameras that write sequentially numbered files therefore land in shooting order either way. The preflight tells you how many photos had no capture time before you commit to the upload.',
  },
  {
    q: 'What image formats can I upload?',
    a: 'JPEG, PNG, HEIC/HEIF, TIFF, WebP, and AVIF. Non-image files in the folder are skipped and reported instead of failing the batch, so you can drop a card directory as-is.',
  },
  {
    q: 'Does my camera have to be a specific brand?',
    a: 'No. The flow only cares that you have stills on disk. GoPro Time Lapse Photo output, trail-camera JPGs, Raspberry Pi captures, phone shots, and mirrorless intervalometer runs are all just a folder of images. If your camera wrote an MP4 instead of stills, upload that video directly and skip the compile step.',
  },
  {
    q: 'Can I choose the output frame rate?',
    a: `Yes — ${FPS_CHOICES}, with ${DEFAULT_FPS_LABEL} as the default. More frames per second plays smoother and makes a shorter video from the same number of photos; the estimated length is shown next to the control before you compile.`,
  },
  {
    q: 'Is it really free?',
    a: `The account is free and includes ${FREE_TIER.storageGb} GB of pooled storage across your projects. Renders on the free plan carry a Tweedia watermark, and raw photos are cleared ${RETENTION_DAYS} days after a project's last activity. Paid plans raise the storage pool and remove the watermark.`,
  },
  {
    q: 'How long does a compile take?',
    a: 'It scales with the number of photos and their size, and it runs on our workers rather than in your browser — you can close the tab and come back. Progress is shown on the Footage tab while it runs, and the project keeps the compiled video when it finishes.',
  },
];

function buildJsonLd() {
  const url = 'https://tweediastudio.com/photo-sequence-to-video';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        name: 'Photo Sequence to Video',
        description:
          'Turn a folder of camera stills into a timelapse video in the browser, ordered by EXIF capture time.',
        url,
        publisher: {
          '@type': 'Organization',
          name: 'Tweedia Studio',
          url: 'https://tweediastudio.com',
        },
      },
      {
        '@type': 'HowTo',
        '@id': `${url}#howto`,
        name: 'How to turn a photo sequence into a timelapse video',
        description:
          'Upload a folder of stills to a Tweedia Studio project, compile them into a video at your chosen frame rate, then edit and render.',
        step: STEPS.map((step, index) => ({
          '@type': 'HowToStep',
          position: index + 1,
          name: step.name,
          url: `${url}#door_how`,
          text: step.text,
        })),
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };
}

const STUDIO_HREF = studioHrefFor(DOOR_PHOTO_SEQUENCE);

function PhotoSequencePage({ user, signIn }) {
  const signedIn = !!user;

  // Funnel plumbing — attribution on arrival, door_view once, section_view per
  // section, and the awaiting-sign-in advance — is shared by every door.
  const { studioHref, setSectionRef, onCtaActivate, onNavClick } = useDoorPage({
    door: DOOR_PHOTO_SEQUENCE,
    signedIn,
    signIn,
    sectionIds: SECTION_IDS,
    meta: {
      title: 'Photo Sequence to Video — Stills to Timelapse | Tweedia Studio',
      description: `Turn a folder of camera stills into a timelapse video in your browser. No per-project frame limit, ${FREE_POOL_LABEL} of free pooled storage, ordered by EXIF capture time.`,
      canonicalPath: '/photo-sequence-to-video',
      jsonLd: buildJsonLd(),
    },
  });

  const renderCta = (ctaId, label, testId) => (
    <DoorCta
      ctaId={ctaId}
      testId={testId}
      signedIn={signedIn}
      studioHref={studioHref}
      onActivate={onCtaActivate}
    >
      {label}
    </DoorCta>
  );

  return (
    <div className="door-page">
      <Header user={user} signIn={signIn} />
      <main className="door-content" data-testid="door-content">
        <section
          className="door-hero"
          id="door_hero"
          data-section-id="door_hero"
          ref={setSectionRef('door_hero')}
        >
          <p className="door-eyebrow">Photo sequence → video</p>
          <h1>Photo Sequence to Video</h1>
          <p className="door-lede">
            Your camera left you a folder of numbered JPGs. Tweedia Studio turns that image
            sequence into a video in the browser — ordered by the time each frame was actually
            captured, with no per-project frame limit and nothing to install.
            Photos to timelapse, in shooting order, on any machine with a browser.
          </p>
          <div className="door-cta-row">
            {renderCta('door_photo_hero', 'Start free — upload your stills', 'door-cta-hero')}
          </div>
          <p className="door-note">
            Free account, no credit card. An account is required before anything is uploaded.
          </p>
        </section>

        <nav className="door-toc" aria-label="On this page">
          <p className="door-toc-title">On this page</p>
          <ol>
            {TOC.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{item.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section
          id="door_situation"
          data-section-id="door_situation"
          ref={setSectionRef('door_situation')}
        >
          <h2>The gap your camera leaves you</h2>
          <p>
            Interval cameras are good at capturing and deliberately silent about what comes next.
            You come back from the shoot, pull the card, and find a directory of sequentially
            numbered stills — sometimes a few hundred, often several thousand, occasionally split
            across folders the camera rotated through on its own.
          </p>
          <p>
            GoPro says it plainly. In Time Lapse <strong>Photo</strong> mode the camera{' '}
            <em>
              saves each still image separately and you can stitch them together yourself with
              your preferred software
            </em>{' '}
            — with no recommendation for what that software should be. The frames are correct and
            the job is unfinished.
          </p>
          <p>
            The usual next stop is a general-purpose image-sequence converter, and that is where
            long shoots fall over: the browser converters are built around drops of a few dozen
            files, and a multi-thousand-frame sequence is simply outside what they accept.
          </p>
        </section>

        <section
          id="door_scale"
          data-section-id="door_scale"
          ref={setSectionRef('door_scale')}
        >
          <h2>How many photos you can actually upload</h2>
          <p>
            <strong>
              No frame limit per project — your account's pooled storage is the only budget.
            </strong>{' '}
            That pool is the ceiling the product enforces, not a figure of speech: the uploader adds
            your batch to everything the account already stores and refuses the overflow before the
            first byte moves, so you find out up front rather than at frame 9,000. Projects
            themselves are unlimited on every plan.
          </p>
          <p className="door-compare" data-testid="door-competitor-caps">
            For contrast: the typical online image-sequence converter caps out around 100 files per
            job, and LRTimelapse's free licence stops at 400 images with 1080p output. A 10-minute
            interval running eight hours a day for a month is about 1,440 frames — past both, and
            a few gigabytes at most.
          </p>
          <p>
            The honest caveat is that the pool is shared. The free plan includes{' '}
            {FREE_POOL_LABEL} across all projects, {SOLO_TIER.name} includes {SOLO_POOL_LABEL} and{' '}
            {PRO_TIER.name} includes {PRO_POOL_LABEL} — and it counts everything you keep: raw
            stills, the compiled source video, and the edits you render. A single 12-megapixel JPG
            runs several megabytes, so on larger shoots the pool, not the frame count, is what you
            plan around. The upload preflight meters it and shows you where you stand before the
            upload starts.
          </p>
        </section>

        <section
          id="door_gopro"
          data-section-id="door_gopro"
          ref={setSectionRef('door_gopro')}
        >
          <h2>GoPro Time Lapse Photo mode</h2>
          <p>
            <strong>The situation:</strong> you shot in Time Lapse Photo (not Time Lapse Video), so
            the card holds one JPG per interval — thousands of them, in GoPro's numbered
            <code> 100GOPRO</code>-style folders. GoPro's own guidance is to stitch them yourself.
          </p>
          <p>
            <strong>The flow:</strong> drop the folders onto a project's Footage tab as they came
            off the card. Every frame carries EXIF capture time, so the sequence rebuilds itself in
            shooting order even if you copied the files in a different order or across several
            drops. Pick a frame rate, compile, and the result becomes the project's source video
            for editing.
          </p>
        </section>

        <section
          id="door_brinno"
          data-section-id="door_brinno"
          ref={setSectionRef('door_brinno')}
        >
          <h2>Brinno timelapse cameras</h2>
          <p>
            <strong>The situation:</strong> Brinno's construction and long-term cameras are usually
            deployed for months, and what you end up holding depends on how the camera was
            configured. Set to save stills, it hands you a card full of JPGs. Set to write its own
            timelapse file, it hands you a clip in a container a general-purpose editor may not
            open.
          </p>
          <p>
            <strong>The flow:</strong> if you have the stills, upload the folder and compile — the
            frame rate choice is yours at that point rather than baked in at capture, which is the
            main practical advantage of having kept the stills. If instead you have an MP4 from the
            camera, upload that video to the project and skip straight to editing.
          </p>
        </section>

        <section
          id="door_trailcam"
          data-section-id="door_trailcam"
          ref={setSectionRef('door_trailcam')}
        >
          <h2>Trail cameras and Wyze cams</h2>
          <p>
            <strong>The situation:</strong> trail cameras in interval or timelapse mode fill the
            card with numbered JPGs, spread across dated folders, and the on-camera software stops
            there. Small Wi-Fi cameras are similar: the frames are easy to collect, and turning
            them into something watchable is left to you.
          </p>
          <p>
            <strong>The flow:</strong> drop every folder from the card in one go. Duplicates are
            detected and skipped, so overlapping folders or a re-drop of the same card cost you
            nothing, and the frames land in capture order rather than folder order. If the camera
            produced an MP4 rather than stills, upload the video instead.
          </p>
        </section>

        <section id="door_pi" data-section-id="door_pi" ref={setSectionRef('door_pi')}>
          <h2>Raspberry Pi camera rigs</h2>
          <p>
            <strong>The situation:</strong> a Pi shooting on a timer writes{' '}
            <code>image0001.jpg</code>, <code>image0002.jpg</code>, and so on, for as long as the
            card holds out. Getting from there to a video usually means an ffmpeg incantation on the
            Pi itself, which is slow, and a numbering scheme that has to line up exactly or the
            command quietly drops frames.
          </p>
          <p>
            <strong>The flow:</strong> copy the directory off the Pi and drop it in. Ordering comes
            from EXIF capture time when the capture tool wrote it, and falls back to the file date
            with the filename as the tiebreak — which is exactly the sequential-numbering case — so
            the sequence is reconstructed without you writing a glob pattern.
          </p>
        </section>

        <section id="door_how" data-section-id="door_how" ref={setSectionRef('door_how')}>
          <h2>How it works, step by step</h2>
          <ol className="door-steps" data-testid="door-steps">
            {STEPS.map((step) => (
              <li key={step.name}>
                <strong>{step.name}.</strong> {step.text}
              </li>
            ))}
          </ol>
        </section>

        <section id="door_free" data-section-id="door_free" ref={setSectionRef('door_free')}>
          <h2>What the free account includes</h2>
          <ul className="door-free-list" data-testid="door-free-list">
            <li>
              <strong>{FREE_POOL_LABEL} of pooled storage</strong> shared across all your
              projects — raw stills, compiled source video and renders all count against it.
            </li>
            <li>
              <strong>Unlimited projects, no per-project frame limit</strong>. The pooled
              allowance is the only ceiling, on every plan.
            </li>
            <li>
              <strong>Raw photos are kept {RETENTION_DAYS} days</strong> after a project's last
              activity on the free plan, then cleared. Renders you have already made are not
              swept — the raw stills are.
            </li>
            <li>
              <strong>Renders carry a Tweedia watermark</strong> on the free plan.
            </li>
          </ul>
          <p>
            Paid plans raise the storage pool, keep your raw photos while the subscription is
            active, and remove the watermark. {SOLO_TIER.name} starts at {SOLO_TIER.priceLabel}
            {SOLO_TIER.priceSuffix}.{' '}
            <Link to="/pricing" onClick={onNavClick('pricing_from_door')}>
              See the plans
            </Link>
            .
          </p>
        </section>

        <section id="door_faq" data-section-id="door_faq" ref={setSectionRef('door_faq')}>
          <h2>FAQ</h2>
          <div className="door-faq" data-testid="door-faq">
            {FAQ.map((item) => (
              <div className="door-faq-item" key={item.q}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section
          className="door-final-cta"
          id="door_final_cta"
          data-section-id="door_final_cta"
          ref={setSectionRef('door_final_cta')}
        >
          <h2>Bring the folder</h2>
          <p>
            Start a project, drop your stills, pick a frame rate. The sequence becomes a video you
            can edit and download in the same place your next shoot will live.
          </p>
          <div className="door-cta-row">
            {renderCta('door_photo_final', 'Create your free account', 'door-cta-final')}
            <Link
              to="/guides/long-term-timelapse"
              className="door-secondary-cta"
              data-testid="door-guide-link"
              onClick={onNavClick('guide_from_door')}
            >
              Read the timelapse guide
            </Link>
          </div>
          <p className="door-note">
            Free renders carry a Tweedia watermark. No credit card required to start.
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default PhotoSequencePage;
export { FAQ, STEPS, SECTION_IDS, STUDIO_HREF };
